import { loadStripe, type Stripe } from '@stripe/stripe-js';
import type { CheckoutSession } from '@loctopus/ticketing-sdk';

let stripePromiseCache: Promise<Stripe | null> | null = null;
let cachedForKey: string | null = null;

/**
 * `CheckoutSession.providerData` is intentionally opaque (packages/payments's
 * `PaymentIntentResult.providerData: Record<string, unknown>`) - this reads a publishable key out
 * of it defensively and falls back to nothing, since docs/API.md does not guarantee the field
 * name. Each tenant can have its own Stripe Connect account with its own publishable key, so this
 * must come from the checkout session response, not a single build-time constant.
 */
export function extractStripePublishableKey(session: CheckoutSession): string | undefined {
  const data = session.providerData;
  if (!data) return undefined;
  const candidate = data.publishableKey ?? data.stripePublishableKey;
  return typeof candidate === 'string' ? candidate : undefined;
}

export function getStripe(publishableKey: string): Promise<Stripe | null> {
  if (stripePromiseCache && cachedForKey === publishableKey) {
    return stripePromiseCache;
  }
  cachedForKey = publishableKey;
  stripePromiseCache = loadStripe(publishableKey);
  return stripePromiseCache;
}
